import { ToolResultOutput, UiStreamAction } from "@intelligems/ig-types";

import {
  ClientToolCall,
  DetectedContinuation,
  Matcher,
  StreamPartAction,
  UiActionResult,
} from "./types";

export function extractUiActionResults(
  output: ToolResultOutput
): UiActionResult | null {
  const { value } = output;
  if (!value || typeof value !== "object" || !("results" in value)) {
    return null;
  }
  const results = (value as { results: unknown }).results;
  if (!results || typeof results !== "object") {
    return null;
  }
  const record = results as UiActionResult;
  if (record.type !== "ui-action") {
    return null;
  }
  return record;
}

/** A `finish` stream message that closes the given turn. */
export function isFinishFor(data: UiStreamAction, turnId: string): boolean {
  return data.type === "finish" && data.turnId === turnId;
}

/**
 * A terminal stream message for the given turn — `finish` (succeeded) or
 * `setError` (failed). Both are scoped to `turnId` so a terminal from an
 * unrelated turn never ends this one.
 */
export function isTerminalFor(data: UiStreamAction, turnId: string): boolean {
  return (
    (data.type === "finish" || data.type === "setError") &&
    data.turnId === turnId
  );
}

/**
 * Match a turn-ending **ui-action** tool's OUTPUT by its `action`. Returns the
 * ui-action payload on a hit, else null. Handlers call this so they own their
 * matching without repeating the stream-part plumbing.
 */
export function matchUiAction(
  part: StreamPartAction,
  action: string
): UiActionResult | null {
  if (part.streamPart.type !== "tool-output-available") {
    return null;
  }
  const results = extractUiActionResults(
    part.streamPart.output as ToolResultOutput
  );
  if (!results || results.action !== action) {
    return null;
  }
  return results;
}

/**
 * Match a parked **`execution: "client"`** tool call by name. Its INPUT arrives
 * as `tool-input-available` with no output (the server left the call open).
 * Returns the call (id + input) on a hit, else null.
 */
export function matchClientTool(
  part: StreamPartAction,
  toolName: string
): ClientToolCall | null {
  if (
    part.streamPart.type !== "tool-input-available" ||
    part.streamPart.toolName !== toolName
  ) {
    return null;
  }
  return {
    toolCallId: part.streamPart.toolCallId,
    input: part.streamPart.input,
  };
}

/**
 * Ask each handler whether it continues this stream message; the first that does
 * gives back its deferred work, which we scope to the message's turn. Only
 * `addStreamPart` messages carry a turn and can trigger a continuation, so we
 * narrow once here — handlers never see other message types or touch `turnId`.
 * Detection owns no trigger knowledge; a new continuation type is just a new
 * handler.
 */
export function detectContinuation(
  data: UiStreamAction,
  handlers: Matcher[]
): DetectedContinuation | null {
  if (data.type !== "addStreamPart") {
    return null;
  }
  for (const handler of handlers) {
    const resolve = handler.match(data);
    if (resolve) {
      return { trigger: data, resolve };
    }
  }
  return null;
}
